import { useState } from 'react';
import { F, Btn } from '../../lib/bricks';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { List, Row, Empty, Section, FieldLabel, TextLink } from '../calm/Calm';
import { STATE_TEXT, connectionMeta, rowMeta, fmtWhen, missingFields } from './steps';
import { visibleImport } from '../../lib/integrations';

const fmt = (v) => new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(v || 0);

function Shell({ open, onClose, title, note, p, children, footer }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent style={{ maxWidth: 460, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>{title}</DialogTitle>
          {note ? <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4, lineHeight: 1.5 }}>{note}</DialogDescription> : null}
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 16 }}>{children}</div>
        <DialogFooter style={{ marginTop: 24, gap: 10 }}>{footer}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DealImport({ data: raw, p, dark, matchOpen, setMatchOpen, connectFor, setConnectFor, call }) {
  const [busy, setBusy] = useState('');
  const [creds, setCreds] = useState({});
  const [picks, setPicks] = useState({});
  const data = visibleImport(raw);
  const conn = data?.connection;
  const sources = data?.sources || [];
  const src = sources.find((s) => s.key === connectFor);
  const run = async (key, fn) => { setBusy(key); try { await fn(); } finally { setBusy(''); } };
  const field = { marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none', boxSizing: 'border-box' };

  const startConnect = (s) => {
    if (!s.fields?.length) return run(s.key, () => call('POST', 'connect', { source: s.key }));
    setCreds({}); setConnectFor(s.key);
  };
  const missing = src ? missingFields(src.fields, creds) : [];

  const connectDialog = (
    <Shell open={!!src} onClose={() => setConnectFor(null)} p={p} title={src ? `Connect ${src.name}` : ''} note={src?.help}
      footer={<>
        <Btn variant="ghost" dark={dark} onClick={() => setConnectFor(null)}>Cancel</Btn>
        <Btn variant="primary" dark={dark} data-testid="import-connect-save" disabled={missing.length > 0 || busy === 'creds'}
          onClick={() => run('creds', async () => { if (await call('POST', 'connect', { source: src.key, credentials: creds })) setConnectFor(null); })}>
          {busy === 'creds' ? 'Checking…' : 'Connect'}
        </Btn>
      </>}>
      {(src?.fields || []).map((f) => (
        <div key={f.key}>
          <FieldLabel htmlFor={`cred-${f.key}`}>{f.label}</FieldLabel>
          <input id={`cred-${f.key}`} data-testid={`cred-${f.key}`} type={f.secret ? 'password' : 'text'} autoComplete="off" spellCheck={false}
            value={creds[f.key] || ''} style={field} onChange={(e) => setCreds((c) => ({ ...c, [f.key]: e.target.value }))} />
        </div>
      ))}
      <div style={{ fontSize: 12.5, color: p.ink3, lineHeight: 1.5 }}>Bricks only reads deals. Nothing changes in {src?.name}. Your details are stored encrypted.</div>
    </Shell>
  );

  if (!conn) {
    return (
      <>
        <Section title="Bring your deals over" testid="import-connect"
          note="Closed and open sales from the last two years come in as transactions. Commissions come in as pending, so nothing posts twice.">
          <List>
            {sources.map((s) => (
              <Row key={s.key} testid={`import-source-${s.key}`} title={s.name}
                meta={s.available ? (s.sandbox ? 'Sandbox office' : null) : s.needs || 'Not switched on yet'}
                aside={s.available ? <TextLink testid={`import-connect-${s.key}`} onClick={() => startConnect(s)}>{busy === s.key ? 'Connecting…' : 'Connect'}</TextLink> : null} />
            ))}
          </List>
        </Section>
        {connectDialog}
      </>
    );
  }

  const rows = data.rows || [];
  const unmatched = data.unmatched || [];
  const agents = data.agents || [];
  return (
    <>
      <Section title="Connected" testid="import-connection">
        <List>
          <Row title={conn.name} meta={connectionMeta(conn)}
            status={conn.status === 'needs_reconnect' ? 'Signed out' : null} attention={conn.status === 'needs_reconnect'}
            aside={<span style={{ display: 'inline-flex', gap: 16 }}>
              {conn.fetched_at && conn.status !== 'needs_reconnect' ? <TextLink testid="import-check-again" onClick={() => run('fetch', () => call('POST', 'fetch'))}>{busy === 'fetch' ? 'Checking…' : 'Check again'}</TextLink> : null}
              <TextLink testid="import-disconnect" onClick={() => run('dc', () => call('DELETE', 'connect'))}>{busy === 'dc' ? 'Disconnecting…' : 'Disconnect'}</TextLink>
            </span>} />
        </List>
      </Section>

      <Section title="Deals" testid="import-rows"
        note={conn.fetched_at ? (unmatched.length ? `Pick who in your office handled ${unmatched.length === 1 ? `${unmatched[0].name}'s deal` : 'these agents\u2019 deals'}. Agents match by email first.` : null) : `Check ${conn.short || conn.name} to see what can come over.`}>
        {!conn.fetched_at ? null : rows.length === 0 ? <Empty>No sales from the last two years.</Empty> : (
          <List>
            {rows.map((r) => (
              <Row key={r.id} testid={`import-row-${r.id}`} title={r.address} meta={rowMeta(r, fmt)}
                status={STATE_TEXT[r.state]} attention={r.state === 'needs_agent' || r.state === 'missing'}
                aside={r.state === 'needs_agent' ? <TextLink testid={`import-pick-${r.id}`} onClick={() => setMatchOpen(true)}>Pick</TextLink> : null} />
            ))}
          </List>
        )}
      </Section>

      {(data.runs || []).length > 0 && (
        <Section title="Recent imports" testid="import-runs">
          <List>
            {data.runs.map((h) => (
              <Row key={h.id} title={`${h.imported} deal${h.imported === 1 ? '' : 's'} from ${h.source}`} meta={[fmtWhen(h.at)]}
                status={h.failed ? `${h.failed} didn't import` : null} attention={h.failed > 0} />
            ))}
          </List>
        </Section>
      )}

      {connectDialog}

      <Shell open={!!matchOpen} onClose={() => setMatchOpen(false)} p={p} title="Match agents"
        note={`These names in ${conn.short || conn.name} didn't match anyone here by email.`}
        footer={<>
          <Btn variant="ghost" dark={dark} onClick={() => setMatchOpen(false)}>Cancel</Btn>
          <Btn variant="primary" dark={dark} data-testid="match-save" disabled={!Object.values(picks).some(Boolean) || busy === 'match'}
            onClick={() => run('match', async () => { if (await call('PUT', 'agents', { agents: picks })) { setMatchOpen(false); setPicks({}); } })}>
            {busy === 'match' ? 'Saving…' : 'Save'}
          </Btn>
        </>}>
        {unmatched.map((u) => (
          <div key={u.key}>
            <FieldLabel htmlFor={`match-${u.key}`}>{u.name}, {u.deals} deal{u.deals === 1 ? '' : 's'}</FieldLabel>
            <select id={`match-${u.key}`} data-testid={`match-${u.key}`} value={picks[u.key] || ''} style={field}
              onChange={(e) => setPicks((s) => ({ ...s, [u.key]: e.target.value }))}>
              <option value="">Choose someone…</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
        ))}
      </Shell>
    </>
  );
}
